import {
  AfterViewInit,
  Component,
  ElementRef,
  ViewChild,
  NgZone,
  OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { CalendarModule } from 'primeng/calendar';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { DropdownModule } from 'primeng/dropdown';
import { CheckboxModule } from 'primeng/checkbox';
import { TabViewModule } from 'primeng/tabview';
import * as mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { circle, booleanPointInPolygon, point } from '@turf/turf';
import { firstValueFrom } from 'rxjs';
import { DateTime } from 'luxon';
import { Router } from '@angular/router';

import {
  OrderStatus,
  OrderClient,
  UpdateOrderStatusDTO,
  DeliveryPlanClient,
  CreateDeliveryPlanDTO,
  UpdateDeliveryStatusDTO,
  UserAccountClient,
  SellerProfileClient
} from 'src/app/base/models/models.swagger.generated';
import { DeliveryDetailsService } from './delivery-details.service';
import {
  DestinationGroup,
  distinctDestinationCount,
  groupDestinations,
  selectVisibleOrders,
  toggleOrder
} from './delivery-planning.logic';
import { ToastService } from 'src/app/base/services/toast.service';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { OrderService } from '../order.service';

interface DeliveryPlan {
  id: string;
  name: string;
  assignedTo?: string;
  assignedToId?: string;
  status: 'draft' | 'out-for-delivery' | 'delivered';
  orderIds: number[];
  comments?: string;
  deliveryDate?: Date;
}

interface OrderWithLocation {
  id?: number;
  orderNumber?: string;
  orderDate?: any;
  status?: OrderStatus;
  buyerId?: string;
  buyerName?: string;
  deliveryAddress?: string;
  contactNumber?: string;
  deliveryInstructions?: string;
  sellerProfileId?: string;
  latitude?: number;
  longitude?: number;
  buyerAddress?: string;
  eligible?: boolean;
  eligibilityReason?: string;
  activeDeliveryPlanName?: string;
  itemCount?: number;
}

type DrawMode = 'circle' | 'polygon' | null;

@Component({
  selector: 'app-delivery-details',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    DialogModule,
    TranslateModule,
    ToastModule,
    CalendarModule,
    InputTextareaModule,
    DropdownModule,
    CheckboxModule,
    TabViewModule
  ],
  providers: [DeliveryDetailsService, OrderClient, DeliveryPlanClient, UserAccountClient, SellerProfileClient],
  templateUrl: './delivery-details.component.html',
  styleUrls: ['./delivery-details.component.scss']
})
export class DeliveryDetailsComponent implements AfterViewInit, OnDestroy {

  allOrders: OrderWithLocation[] = [];
  pendingOrders: OrderWithLocation[] = [];
  filteredPendingOrders: OrderWithLocation[] = [];
  applicationUsers: any[] = [];
  searchQuery = '';
  showSearch = false;
  selectedOrderIds = new Set<number>();
  private orderCache = new Map<number, OrderWithLocation>();
  eligibleSkip = 0;
  readonly eligibleTake = 50;
  eligibleTotal = 0;
  ordersLoading = false;
  ordersLoadError = '';
  shopId: number | null = null;
  fromDate = '';
  filterToDate = '';
  shops: { id?: number; name?: string }[] = [];
  assignmentFilter: 'unassigned' | 'assigned' = 'unassigned';
  statusFilter: number | null = null;
  showPlanConfirmation = false;
  destinationGroups: DestinationGroup[] = [];

  deliveryPlans: DeliveryPlan[] = [];
  selectedDeliveryPlan: DeliveryPlan | null = null;
  private deliveryPlanMap: Map<string, number> = new Map();

  selectedOrder: OrderWithLocation | null = null;
  highlightedOrderId: number | null = null;

  showOrderDetailsDialog = false;
  showNewDeliveryDialog = false;
  showNewDeliveryForm = false;
  isFormMinimized = false;
  activeTabIndex = 0;
  today = new Date();
  newDeliveryPlan = {
    name: '',
    deliveryDate: new Date(),
    assignedToId: '',
    orderIds: [] as number[],
    comments: ''
  };

  selectedOrderToAdd: number | null = null;
  availableOrdersForNewPlan: any[] = [];
  availableOrdersForEditPlan: any[] = [];

  isMapFullScreen = false;
  isDrawMode = false;
  currentDrawMode: DrawMode = null;
  public deliveryPlanFilter: 'active' | 'completed' = 'active';

  @ViewChild('searchInput', { static: false })
  searchInput!: ElementRef<HTMLInputElement>;

  @ViewChild('mapContainer', { static: false })
  mapContainer!: ElementRef<HTMLDivElement>;

  private map!: mapboxgl.Map;
  private markers: Map<number, mapboxgl.Marker> = new Map();
  private draw!: MapboxDraw;
  private _sub = new Subscription();

  constructor(
    private deliveryDetailsService: DeliveryDetailsService,
    private sellerProfileClient: SellerProfileClient,
    private orderClient: OrderClient,
    private ngZone: NgZone,
    private toastService: ToastService,
    private router: Router,
    private orderService: OrderService
  ) {
    this.initializeDeliveryPlans();
  }

  private initializeDeliveryPlans(): void {
    this.deliveryDetailsService.searchDeliveryPlans().subscribe({
      next: (result) => {
        if (result?.items) {
          this.deliveryPlans = result.items.map((plan: any) => ({
            id: plan.id?.toString() || `plan_${Date.now()}`,
            name: plan.name || '',
            status: this.mapDeliveryStatus(plan.status),
            orderIds: plan.details?.map((d: any) => d.orderId) || [],
            assignedTo: plan.assignedToName,
            assignedToId: plan.assignedToId,
            comments: plan.comments,
            deliveryDate: plan.deliveryDate?.toJSDate ? plan.deliveryDate.toJSDate() : (plan.deliveryDate ? new Date(plan.deliveryDate) : undefined)
          }));
          this.deliveryPlanMap.clear();
          this.deliveryPlans.forEach(plan => {
            this.deliveryPlanMap.set(plan.id, parseInt(plan.id));
          });
        }
      },
      error: () => { }
    });
    this.loadApplicationUsers();
  }



  private loadApplicationUsers(): void {
    this.deliveryDetailsService.getDeliveryExecutives().subscribe({
      next: (users) => {
        this.applicationUsers = users.map(u => ({
          userId: u.userId,
          displayName: `${u.firstname} ${u.lastname}`
        }));
      },
      error: () => { }
    });
  }


  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.initMap();
    });

    this._sub.add(
      this.orderService.orderStatusChanged$.subscribe(() => {
        this.refreshDeliveryPlans();
        this.loadOrders();
      })
    );
    this.sellerProfileClient.getMySellerProfiles().subscribe({
      next: (profiles) => this.shops = profiles || [],
      error: () => this.shops = []
    });
  }


  private refreshDeliveryPlans(): void {
    this.deliveryDetailsService.searchDeliveryPlans().subscribe({
      next: (result) => {
        if (result?.items) {
          this.deliveryPlans = result.items.map((plan: any) => ({
            id: plan.id?.toString() || `plan_${Date.now()}`,
            name: plan.name || '',
            status: this.mapDeliveryStatus(plan.status),
            orderIds: plan.details?.map((d: any) => d.orderId) || [],
            assignedTo: plan.assignedToName,
            assignedToId: plan.assignedToId,
            comments: plan.comments,
            deliveryDate: plan.deliveryDate?.toJSDate
              ? plan.deliveryDate.toJSDate()
              : plan.deliveryDate ? new Date(plan.deliveryDate) : undefined
          }));

          // ← Add this
          this.deliveryPlanMap.clear();
          this.deliveryPlans.forEach(plan => {
            this.deliveryPlanMap.set(plan.id, parseInt(plan.id));
          });

          if (this.selectedDeliveryPlan) {
            const updated = this.deliveryPlans.find(
              p => p.id === this.selectedDeliveryPlan!.id
            );
            if (updated) {
              this.selectedDeliveryPlan = { ...updated };
            }
          }
        }
      },
      error: () => { }
    });
  }

  private mapDeliveryStatus(status: number): 'draft' | 'out-for-delivery' | 'delivered' {
    switch (status) {
      case 0: return 'draft';
      case 1: return 'out-for-delivery';
      case 2: return 'out-for-delivery';
      case 3: return 'delivered';
      case 4: return 'draft';
      default: return 'draft';
    }
  }

  ngOnDestroy(): void {
    this._sub.unsubscribe();
  }

  private initMap(): void {
    this.map = new mapboxgl.Map({
      container: this.mapContainer.nativeElement,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [77.5946, 12.9716],
      zoom: 11,
      accessToken: 'pk.eyJ1IjoibmFnYmh1c2hhbiIsImEiOiJjbWwwaXZpNnYwY2Y1M2VzYThqaTlraTAyIn0.oA1w174W9md-VPNqj_b67g',
      attributionControl: false
    });

    this.map.addControl(new mapboxgl.NavigationControl());

    this.draw = new MapboxDraw({
      displayControlsDefault: false,
      controls: {},
      modes: {
        ...MapboxDraw.modes,
        draw_circle: CircleMode
      }
    });

    this.map.addControl(this.draw as any);

    this.map.on('draw.create', (e: any) => {
      this.ngZone.run(() => {
        this.onDrawCreate(e);
      });
    });

    this.map.on('draw.update', (e: any) => {
      this.ngZone.run(() => {
        this.onDrawUpdate(e);
      });
    });

    this.map.on('load', () => {
      this.ngZone.run(() => {
        this.loadOrders();
      });
    });
  }

  private onDrawCreate(e: any): void {
    this.processDrawnShape(e.features[0]);
    this.draw.deleteAll();
    this.exitDrawMode();
  }

  private onDrawUpdate(e: any): void {
    this.processDrawnShape(e.features[0]);
  }

  private processDrawnShape(feature: any): void {
    if (!this.selectedDeliveryPlan) {
      this.toastService.showError(
        'i18n.domain.deliveryDetails.messages.error-title',
        'Please select a delivery plan first'
      );
      return;
    }

    if (!feature || !feature.geometry) return;

    const ordersInShape = this.getOrdersWithinShape(feature);

    if (ordersInShape.length === 0) {
      this.toastService.showError(
        'i18n.domain.deliveryDetails.messages.info-title',
        'i18n.domain.deliveryDetails.messages.no-orders-in-area'
      );
      return;
    }

    ordersInShape.forEach(order => {
      if (order.id && !this.selectedDeliveryPlan!.orderIds.includes(order.id)) {
        this.selectedDeliveryPlan!.orderIds.push(order.id);
        this.ngZone.runOutsideAngular(() => this.updateMarkerStyle(order.id!));
      }
    });

    this.updateAvailableOrdersForEditPlan();

    this.toastService.showSuccess(
      'i18n.domain.deliveryDetails.messages.success-title',
      `Added ${ordersInShape.length} order(s) to ${this.selectedDeliveryPlan!.name}`
    );
  }

  private getOrdersWithinShape(feature: any): OrderWithLocation[] {
    const ordersWithCoords = this.allOrders.filter(
      o => o.latitude != null && o.longitude != null
    );

    if (feature.geometry.type === 'Polygon') {
      return ordersWithCoords.filter(order => {
        const pt = point([order.longitude!, order.latitude!]);
        try {
          return booleanPointInPolygon(pt, feature);
        } catch (e) {
          return this.isPointInPolygon(
            [order.longitude!, order.latitude!],
            feature.geometry.coordinates[0]
          );
        }
      });
    }

    if (feature.properties && feature.properties.center && feature.properties.radiusInKm) {
      const center = feature.properties.center;
      const radius = feature.properties.radiusInKm * 1000;

      return ordersWithCoords.filter(order => {
        const distance = this.getDistance(
          center[1], center[0],
          order.latitude!, order.longitude!
        );
        return distance <= radius;
      });
    }

    return [];
  }

  private isPointInPolygon(point: number[], polygon: number[][]): boolean {
    const x = point[0];
    const y = point[1];
    let inside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = polygon[i][0];
      const yi = polygon[i][1];
      const xj = polygon[j][0];
      const yj = polygon[j][1];

      const intersect = ((yi > y) !== (yj > y)) &&
        (x < (xj - xi) * (y - yi) / (yj - yi) + xi);

      if (intersect) inside = !inside;
    }

    return inside;
  }

  private getDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371e3;
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
      Math.cos(φ1) * Math.cos(φ2) *
      Math.sin(Δλ / 2) * Math.sin(Δλ / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  }

  toggleDrawCircle(): void {
    if (!this.selectedDeliveryPlan) {
      this.toastService.showError(
        'i18n.domain.deliveryDetails.messages.error-title',
        'Please select a delivery plan first'
      );
      return;
    }

    if (this.isDrawMode && this.currentDrawMode === 'circle') {
      this.exitDrawMode();
    } else {
      this.currentDrawMode = 'circle';
      this.isDrawMode = true;
      this.draw.changeMode('draw_circle' as any);
    }
  }

  toggleDrawPolygon(): void {
    if (!this.selectedDeliveryPlan) {
      this.toastService.showError(
        'i18n.domain.deliveryDetails.messages.error-title',
        'Please select a delivery plan first'
      );
      return;
    }

    if (this.isDrawMode && this.currentDrawMode === 'polygon') {
      this.exitDrawMode();
    } else {
      this.currentDrawMode = 'polygon';
      this.isDrawMode = true;
      this.draw.changeMode('draw_polygon');
    }
  }

  private exitDrawMode(): void {
    this.isDrawMode = false;
    this.currentDrawMode = null;
    this.draw.changeMode('simple_select');
    this.draw.deleteAll();
  }

  cancelDrawMode(): void {
    this.exitDrawMode();
  }

  private loadOrders(): void {
    this.ordersLoading = true;
    this.ordersLoadError = '';
    this.deliveryDetailsService.getEligibleDeliveryOrders({
      sellerProfileId: this.shopId ?? undefined,
      search: this.searchQuery.trim() || undefined,
      from: this.fromDate ? new Date(this.fromDate).toISOString() : undefined,
      to: this.filterToDate ? new Date(`${this.filterToDate}T23:59:59`).toISOString() : undefined,
      status: this.statusFilter ?? undefined,
      assignment: this.assignmentFilter,
      eligibleOnly: this.assignmentFilter !== 'assigned',
      skip: this.eligibleSkip,
      take: this.eligibleTake
    }).subscribe({
      next: (page) => {
        const items = page?.items || [];
        this.eligibleTotal = page?.totalNumberOfItems || 0;
        this.filteredPendingOrders = items.map((order) => this.toPlanningOrder(order));
        this.filteredPendingOrders.forEach((order) => {
          if (order.id != null) this.orderCache.set(order.id, order);
        });
        this.pendingOrders = this.filteredPendingOrders;
        this.allOrders = [...this.orderCache.values()];
        this.destinationGroups = groupDestinations(this.filteredPendingOrders
          .filter((order) => order.id != null)
          .map((order) => ({ id: order.id!, address: order.deliveryAddress, eligible: order.eligible })));
        this.ordersLoading = false;
        this.ngZone.runOutsideAngular(() => this.plotOrdersOnMap());
      },
      error: () => {
        this.ordersLoading = false;
        this.ordersLoadError = 'Eligible orders could not be loaded.';
        this.filteredPendingOrders = [];
        this.destinationGroups = [];
      }
    });
  }

  private toPlanningOrder(order: any): OrderWithLocation {
    return {
      id: order.orderId,
      orderNumber: order.orderNumber,
      orderDate: order.orderDate,
      status: order.status,
      buyerId: order.buyerId,
      buyerName: order.buyerName || 'Buyer',
      deliveryAddress: order.deliveryAddress,
      buyerAddress: order.deliveryAddress,
      sellerProfileId: order.sellerProfileId?.toString(),
      eligible: order.eligible,
      eligibilityReason: order.eligibilityReason,
      activeDeliveryPlanName: order.activeDeliveryPlanName,
      itemCount: order.itemCount
    };
  }

  reloadEligibleOrders(): void {
    this.eligibleSkip = 0;
    this.loadOrders();
  }

  nextEligiblePage(): void {
    if (this.eligibleSkip + this.eligibleTake >= this.eligibleTotal) return;
    this.eligibleSkip += this.eligibleTake;
    this.loadOrders();
  }

  previousEligiblePage(): void {
    this.eligibleSkip = Math.max(0, this.eligibleSkip - this.eligibleTake);
    this.loadOrders();
  }

  isOrderSelected(orderId?: number): boolean {
    return orderId != null && this.selectedOrderIds.has(orderId);
  }

  toggleOrderSelection(order: OrderWithLocation, checked: boolean): void {
    if (order.id == null || order.eligible === false) return;
    this.selectedOrderIds = toggleOrder(this.selectedOrderIds, order.id, checked);
  }

  selectVisibleEligibleOrders(): void {
    this.selectedOrderIds = selectVisibleOrders(this.selectedOrderIds, this.filteredPendingOrders
      .filter((order) => order.id != null)
      .map((order) => ({ id: order.id!, address: order.deliveryAddress, eligible: order.eligible !== false })));
  }

  clearOrderSelection(): void {
    this.selectedOrderIds = new Set();
  }

  get selectedOrderCount(): number {
    return this.selectedOrderIds.size;
  }

  get confirmationDestinationCount(): number {
    return distinctDestinationCount(
      [...this.orderCache.values()].filter((order) => order.id != null)
        .map((order) => ({ id: order.id!, address: order.deliveryAddress })),
      this.selectedOrderIds);
  }

  get selectedShopName(): string {
    if (this.shopId == null) return 'All shops';
    return this.shops.find((shop) => shop.id === this.shopId)?.name || 'Selected shop';
  }

  toggleDestinationGroup(group: DestinationGroup): void {
    const allSelected = group.orderIds.every((id) => this.selectedOrderIds.has(id));
    group.orderIds.forEach((id) => {
      const order = this.filteredPendingOrders.find((item) => item.id === id);
      if (order?.eligible === false) return;
      this.selectedOrderIds = toggleOrder(this.selectedOrderIds, id, !allSelected);
    });
  }

  openPlanConfirmation(): void {
    if (this.selectedOrderIds.size === 0) {
      this.toastService.showError(
        'i18n.domain.deliveryDetails.messages.error-title',
        'Select at least one eligible order.'
      );
      return;
    }
    this.showPlanConfirmation = true;
  }

  confirmCreatePlan(): void {
    if (!this.newDeliveryPlan.name?.trim()) {
      this.toastService.showError(
        'i18n.domain.deliveryDetails.messages.error-title',
        'Please enter a delivery plan name'
      );
      return;
    }

    const dto = new CreateDeliveryPlanDTO({
      name: this.newDeliveryPlan.name,
      deliveryDate: this.newDeliveryPlan.deliveryDate
        ? DateTime.fromJSDate(this.newDeliveryPlan.deliveryDate)
        : undefined,
      assignedToId: this.newDeliveryPlan.assignedToId || undefined,
      comments: this.newDeliveryPlan.comments || undefined,
      orderIds: [...this.selectedOrderIds]
    });

    this.deliveryDetailsService.createDeliveryPlan(dto).subscribe({
      next: (plan) => {
        const localPlan: DeliveryPlan = {
          id: plan.id?.toString() || `plan_${Date.now()}`,
          name: plan.name || '',
          status: 'draft',
          orderIds: [...this.selectedOrderIds],
          assignedTo: this.applicationUsers.find(u => u.userId === plan.assignedToId)?.displayName,
          assignedToId: plan.assignedToId,
          comments: plan.comments,
          deliveryDate: plan.deliveryDate?.toJSDate()
        };
        this.deliveryPlanMap.set(localPlan.id, plan.id || 0);
        this.deliveryPlans.push(localPlan);
        this.selectedDeliveryPlan = localPlan;
        this.showPlanConfirmation = false;
        this.clearOrderSelection();
        this.loadOrders();
        this.toastService.showSuccess(
          'i18n.domain.deliveryDetails.messages.success-title',
          'i18n.domain.deliveryDetails.messages.delivery-plan-created'
        );
      },
      error: () => {
        this.toastService.showError(
          'i18n.domain.deliveryDetails.messages.error-title',
          'One or more selected orders could not be added. Review the selection and try again.'
        );
      }
    });
  }

  fitSelectedDestinations(): void {
    const selected = this.filteredPendingOrders.filter((order) =>
      order.id != null && this.selectedOrderIds.has(order.id) && order.latitude != null && order.longitude != null);
    if (!selected.length || !this.map) {
      this.toastService.showSuccess(
        'i18n.domain.deliveryDetails.messages.info-title',
        'These stops are confirmed addresses. The map can fit them when a confirmed coordinate is stored on the order.'
      );
      return;
    }
    const bounds = new mapboxgl.LngLatBounds();
    selected.forEach((order) => bounds.extend([order.longitude!, order.latitude!]));
    this.map.fitBounds(bounds, { padding: 80, maxZoom: 14 });
  }

  applySearchFilter(): void {
    this.reloadEligibleOrders();
  }

  viewAllOrders(event: Event): void {
    event.preventDefault();
    this.router.navigate(['/order']);
  }

  toggleMapFullScreen(): void {
    this.isMapFullScreen = !this.isMapFullScreen;
    setTimeout(() => {
      this.ngZone.runOutsideAngular(() => this.map.resize());
    }, 100);
  }

  toggleSearch(): void {
    this.showSearch = !this.showSearch;

    if (this.showSearch) {
      setTimeout(() => {
        if (this.searchInput) {
          this.searchInput.nativeElement.focus();
        }
      }, 100);
    } else {
      this.clearSearch();
    }
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.applySearchFilter();
  }

  private plotOrdersOnMap(): void {
    this.clearMarkers();

    const ordersWithLocation = this.allOrders.filter(
      (o) => o.latitude != null && o.longitude != null
    );

    if (!ordersWithLocation.length) return;

    ordersWithLocation.forEach((order) => this.createMarker(order));
    this.fitBounds();

    setTimeout(() => this.map.resize(), 100);
  }

  private createMarker(order: OrderWithLocation): void {
    const getColor = (): string => {
      if (this.highlightedOrderId === order.id) {
        return '#ef4444';
      }

      if (order.status === OrderStatus.OutForDelivery) {
        return '#FF5722';
      } else if (order.status === OrderStatus.Pending) {
        return '#FFC107';
      }

      return '#4a90e2';
    };

    const svg = `
    <svg width="32" height="42" viewBox="0 0 32 42"
         xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow-${order.id}" x="-50%" y="-50%"
                width="200%" height="200%">
          <feDropShadow dx="0" dy="2"
                        stdDeviation="3"
                        flood-opacity="0.3"/>
        </filter>
      </defs>

      <g filter="url(#shadow-${order.id})">
        <path class="pin-body"
          d="M16 0C10.5 0 6 4.5 6 10c0 8 10 22 10 22
             s10-14 10-22c0-5.5-4.5-10-10-10z"
          fill="${getColor()}"
          stroke="rgba(0,0,0,0.15)"
          stroke-width="0.5"/>
      </g>

      <circle cx="16" cy="10" r="6" fill="white"/>
    </svg>
  `;

    const el = document.createElement('div');
    el.className = 'custom-map-marker';
    el.innerHTML = svg;
    el.style.width = '32px';
    el.style.height = '42px';
    el.style.cursor = 'pointer';

    el.addEventListener('click', (e) => {
      e.stopPropagation();
      this.ngZone.run(() => this.onMarkerClick(order));
    });

    el.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.ngZone.run(() => this.showContextMenu(order, e));
    });

    const marker = new mapboxgl.Marker({
      element: el,
      anchor: 'bottom'
    })
      .setLngLat([order.longitude!, order.latitude!])
      .addTo(this.map);

    this.markers.set(order.id!, marker);
  }

  private onMarkerClick(order: OrderWithLocation): void {
    this.selectedOrder = order;
    this.showOrderDetailsDialog = true;
  }

  private updateMarkerStyle(orderId: number): void {
    const marker = this.markers.get(orderId);
    if (!marker) return;

    const order = this.allOrders.find(o => o.id === orderId);
    if (!order) return;

    const el = marker.getElement();
    const isHighlighted = this.highlightedOrderId === orderId;

    let color = '#4a90e2';
    if (isHighlighted) {
      color = '#ef4444';
    } else if (order.status === OrderStatus.OutForDelivery) {
      color = '#FF5722';
    } else if (order.status === OrderStatus.Pending) {
      color = '#FFC107';
    }

    const path = el.querySelector('.pin-body') as SVGPathElement;
    if (path) {
      path.setAttribute('fill', color);
    }
  }

  private updateAllMarkerStyles(): void {
    this.ngZone.runOutsideAngular(() => {
      this.markers.forEach((_, orderId) => {
        this.updateMarkerStyle(orderId);
      });
    });
  }

  private showContextMenu(order: OrderWithLocation, event: MouseEvent): void {
    document.querySelector('.context-menu')?.remove();

    const menu = document.createElement('div');
    menu.className = 'context-menu';
    menu.style.position = 'fixed';
    menu.style.left = `${event.clientX}px`;
    menu.style.top = `${event.clientY}px`;

    if (this.isOrderInAnyDeliveryPlan(order.id!)) {
      this.addMenuItem(menu, 'Remove from Delivery', () => {
        this.removeOrderFromDelivery(order.id!);
        menu.remove();
      });
    } else {
      this.addMenuItem(menu, 'Add to Delivery', () => {
        this.showAddToDeliveryOptions(order, menu);
      });
    }

    document.body.appendChild(menu);

    const closeMenu = (e: MouseEvent) => {
      if (!menu.contains(e.target as Node)) {
        menu.remove();
        document.removeEventListener('click', closeMenu);
      }
    };
    setTimeout(() => document.addEventListener('click', closeMenu), 0);
  }

  private showAddToDeliveryOptions(
    order: OrderWithLocation,
    parentMenu: HTMLElement
  ): void {
    parentMenu.innerHTML = '';

    const header = document.createElement('div');
    header.className = 'context-menu-header';
    header.textContent = 'Select Delivery Plan';
    parentMenu.appendChild(header);


    const draftPlans = this.deliveryPlans.filter(p => p.status === 'draft');

    if (draftPlans.length === 0) {
      const noPlans = document.createElement('div');
      noPlans.className = 'context-menu-item disabled';
      noPlans.textContent = 'No draft delivery plans available';
      parentMenu.appendChild(noPlans);
    } else {
      draftPlans.forEach((plan) => {
        this.addMenuItem(parentMenu, plan.name, () => {
          this.addOrderToDeliveryPlan(order.id!, plan.id);
          parentMenu.remove();
        });
      });
    }
  }

  private addMenuItem(parent: HTMLElement, label: string, onClick: () => void): void {
    const item = document.createElement('div');
    item.className = 'context-menu-item';
    item.textContent = label;
    item.addEventListener('click', (e) => {
      e.stopPropagation();
      this.ngZone.run(() => {
        onClick();
      });
    });
    parent.appendChild(item);
  }

  private isOrderInAnyDeliveryPlan(orderId: number): boolean {
    return this.deliveryPlans.some((plan) => plan.orderIds.includes(orderId));
  }

  private addOrderToDeliveryPlan(orderId: number, planId: string): void {
    const plan = this.deliveryPlans.find((p) => p.id === planId);
    if (!plan) {
      console.error('Delivery plan not found:', planId);
      return;
    }

    if (plan.orderIds.includes(orderId)) {
      this.toastService.showError(
        'i18n.domain.deliveryDetails.messages.info-title',
        'i18n.domain.deliveryDetails.messages.order-already-in-plan'
      );
      return;
    }

    plan.orderIds.push(orderId);
    this.selectedDeliveryPlan = plan;

    const order = this.allOrders.find(o => o.id === orderId);
    const orderName = order ? order.orderNumber : `Order #${orderId}`;

    this.toastService.showSuccess(
      'i18n.domain.deliveryDetails.messages.success-title',
      `Added ${orderName} to ${plan.name}`
    );

    this.ngZone.runOutsideAngular(() => {
      this.updateMarkerStyle(orderId);
    });
  }

  removeOrderFromDelivery(orderId: number): void {
    this.deliveryPlans.forEach((plan) => {
      const idx = plan.orderIds.indexOf(orderId);
      if (idx > -1) plan.orderIds.splice(idx, 1);
    });
    if (this.selectedDeliveryPlan) {
      const idx = this.selectedDeliveryPlan.orderIds.indexOf(orderId);
      if (idx > -1) this.selectedDeliveryPlan.orderIds.splice(idx, 1);
    }

    this.updateAvailableOrdersForEditPlan();
    this.ngZone.runOutsideAngular(() => this.updateMarkerStyle(orderId));

    this.toastService.showSuccess(
      'i18n.domain.deliveryDetails.messages.success-title',
      'i18n.domain.deliveryDetails.messages.order-removed-from-plan'
    );
  }

  addOrderToSelectedPlan(orderId: number | null): void {
    if (!orderId || !this.selectedDeliveryPlan) return;

    if (this.selectedDeliveryPlan.orderIds.includes(orderId)) {
      this.toastService.showError(
        'i18n.domain.deliveryDetails.messages.info-title',
        'Order already in this plan'
      );
      this.selectedOrderToAdd = null;
      return;
    }

    this.selectedDeliveryPlan.orderIds.push(orderId);
    this.updateAvailableOrdersForEditPlan();

    const order = this.allOrders.find(o => o.id === orderId);
    const orderName = order ? order.orderNumber : `Order #${orderId}`;

    this.toastService.showSuccess(
      'i18n.domain.deliveryDetails.messages.success-title',
      `Added ${orderName} to ${this.selectedDeliveryPlan.name}`
    );

    this.selectedOrderToAdd = null;
  }

  updateAvailableOrdersForEditPlan(): void {
    if (!this.selectedDeliveryPlan) {
      this.availableOrdersForEditPlan = [];
      return;
    }

    const availableOrders = this.pendingOrders.filter(order =>
      !this.selectedDeliveryPlan!.orderIds.includes(order.id!)
    );

    this.availableOrdersForEditPlan = availableOrders.map(order => ({
      id: order.id,
      orderNumber: order.orderNumber,
      buyerName: order.buyerName,
      buyerAddress: order.buyerAddress,
      displayText: `${order.orderNumber} - ${order.buyerName} (${order.buyerAddress})`
    }));
  }

  onPendingOrderClick(order: OrderWithLocation): void {
    this.highlightedOrderId = order.id!;
    this.updateAllMarkerStyles();

    if (order.latitude && order.longitude) {
      this.ngZone.runOutsideAngular(() => {
        this.map.flyTo({
          center: [order.longitude!, order.latitude!],
          zoom: 15,
          duration: 1000
        });
      });
    }
  }

  onDeliveryPlanClick(plan: DeliveryPlan): void {
    this.showNewDeliveryForm = false;
    this.isFormMinimized = false;
    this.activeTabIndex = 0;

    const planId = this.deliveryPlanMap.get(plan.id) || parseInt(plan.id);
    this.deliveryDetailsService.getDeliveryPlan(planId).subscribe({
      next: (fullPlan) => {
        this.selectedDeliveryPlan = {
          id: fullPlan.id?.toString() || plan.id,
          name: fullPlan.name || '',
          status: this.mapDeliveryStatus(fullPlan.status),
          orderIds: fullPlan.details?.map((d: any) => d.orderId) || [],
          assignedTo: fullPlan.assignedToName,
          assignedToId: fullPlan.assignedToId,
          comments: fullPlan.comments,
          deliveryDate: this.toDate(fullPlan.deliveryDate)
        };
        this.updateAvailableOrdersForEditPlan();
        this.highlightDeliveryPlanOrders(this.selectedDeliveryPlan);
      }
    });
  }


  get isSelectedPlanReadOnly(): boolean {
    return this.selectedDeliveryPlan?.status === 'out-for-delivery'
      || this.selectedDeliveryPlan?.status === 'delivered';
  }
  get activePlans(): DeliveryPlan[] {
    return this.deliveryPlans.filter(p => p.status !== 'delivered');
  }

  get completedPlans(): DeliveryPlan[] {
    return this.deliveryPlans.filter(p => p.status === 'delivered');
  }
  get filteredDeliveryPlans(): DeliveryPlan[] {
    return this.deliveryPlanFilter === 'active'
      ? this.activePlans
      : this.completedPlans;
  }


  private highlightDeliveryPlanOrders(plan: DeliveryPlan): void {
    this.ngZone.runOutsideAngular(() => {
      this.markers.forEach((marker, orderId) => {
        marker.getElement().classList.toggle('in-selected-plan', plan.orderIds.includes(orderId));
      });

      const planOrders = this.allOrders.filter((o) => plan.orderIds.includes(o.id!));
      if (planOrders.length > 0) {
        const bounds = new mapboxgl.LngLatBounds();
        planOrders.forEach((o) => {
          if (o.latitude && o.longitude) bounds.extend([o.longitude, o.latitude]);
        });
        this.map.fitBounds(bounds, { padding: 80, maxZoom: 14 });
      }
    });
  }

  openNewDeliveryDialog(): void {
    this.selectedDeliveryPlan = null;
    this.showNewDeliveryForm = true;
    this.isFormMinimized = false;

    this.newDeliveryPlan = {
      name: '',
      deliveryDate: new Date(),
      assignedToId: '',
      orderIds: [],
      comments: ''
    };
  }

  cancelNewDeliveryDialog(): void {
    this.showNewDeliveryForm = false;
    this.isFormMinimized = false;
    this.newDeliveryPlan = {
      name: '',
      deliveryDate: new Date(),
      assignedToId: '',
      orderIds: [],
      comments: ''
    };
  }


  createNewDeliveryPlan(): void {
    if (!this.newDeliveryPlan.name?.trim()) {
      this.toastService.showError(
        'i18n.domain.deliveryDetails.messages.error-title',
        'Please enter a delivery plan name'
      );
      return;
    }

    const dto = new CreateDeliveryPlanDTO({
      name: this.newDeliveryPlan.name,
      deliveryDate: this.newDeliveryPlan.deliveryDate
        ? DateTime.fromJSDate(this.newDeliveryPlan.deliveryDate)
        : undefined,
      assignedToId: this.newDeliveryPlan.assignedToId || undefined,
      comments: this.newDeliveryPlan.comments || undefined,
      orderIds: []
    });

    this.deliveryDetailsService.createDeliveryPlan(dto).subscribe({
      next: (plan) => {
        const localPlan: DeliveryPlan = {
          id: plan.id?.toString() || `plan_${Date.now()}`,
          name: plan.name || '',
          status: 'draft',
          orderIds: [],
          assignedTo: this.applicationUsers.find(u => u.userId === plan.assignedToId)?.displayName,
          assignedToId: plan.assignedToId,
          comments: plan.comments,
          deliveryDate: plan.deliveryDate?.toJSDate()
        };
        this.deliveryPlanMap.set(localPlan.id, plan.id || 0);
        this.deliveryPlans.push(localPlan);

        this.selectedDeliveryPlan = localPlan;
        this.showNewDeliveryForm = false;
        this.isFormMinimized = false;
        this.activeTabIndex = 0;
        this.updateAvailableOrdersForEditPlan();

        this.toastService.showSuccess(
          'i18n.domain.deliveryDetails.messages.success-title',
          'Draft created successfully! Now you can add orders.'
        );
      },
      error: () => {
        this.toastService.showError(
          'i18n.domain.deliveryDetails.messages.error-title',
          'Failed to create delivery plan'
        );
      }
    });
  }

  closeDeliveryForm(): void {
    this.selectedDeliveryPlan = null;
    this.showNewDeliveryForm = false;
    this.isFormMinimized = false;

    this.ngZone.runOutsideAngular(() => {
      this.markers.forEach((marker) => {
        marker.getElement().classList.remove('in-selected-plan');
      });
    });
  }

  toggleMinimizeForm(): void {
    this.isFormMinimized = !this.isFormMinimized;
  }

  deleteDeliveryPlan(): void {
    if (!this.selectedDeliveryPlan) return;
    if (this.selectedDeliveryPlan.status !== 'draft') {
      this.toastService.showError(
        'i18n.domain.deliveryDetails.messages.error-title',
        'i18n.domain.deliveryDetails.messages.cannot-delete-submitted'
      );
      return;
    }

    const planId = this.deliveryPlanMap.get(this.selectedDeliveryPlan.id) || 0;
    this.deliveryDetailsService.deleteDeliveryPlan(planId).subscribe({
      next: () => {
        const idx = this.deliveryPlans.findIndex(p => p.id === this.selectedDeliveryPlan!.id);
        if (idx > -1) this.deliveryPlans.splice(idx, 1);

        this.toastService.showSuccess(
          'i18n.domain.deliveryDetails.messages.success-title',
          'i18n.domain.deliveryDetails.messages.delivery-plan-deleted'
        );

        this.closeDeliveryForm();
      },
      error: () => {
        this.toastService.showError(
          'i18n.domain.deliveryDetails.messages.error-title',
          'i18n.domain.deliveryDetails.messages.delete-failed'
        );
      }
    });
  }

  saveDraft(): void {
    if (!this.selectedDeliveryPlan) return;

    const planId = this.deliveryPlanMap.get(this.selectedDeliveryPlan.id) || 0;
    const dto = new UpdateDeliveryStatusDTO({
      name: this.selectedDeliveryPlan.name,
      assignedToId: this.selectedDeliveryPlan.assignedToId || undefined,
      comments: this.selectedDeliveryPlan.comments,
      orderIds: this.selectedDeliveryPlan.orderIds,
      deliveryDate: this.selectedDeliveryPlan.deliveryDate
        ? DateTime.fromJSDate(this.selectedDeliveryPlan.deliveryDate)
        : undefined
    });

    this.deliveryDetailsService.updateDeliveryPlan(planId, dto).subscribe({
      next: () => {
        this.toastService.showSuccess(
          'i18n.domain.deliveryDetails.messages.draft-saved-title',
          'Draft saved successfully'
        );
        this.closeDeliveryForm();
      },
      error: () => {
        this.toastService.showError(
          'i18n.domain.deliveryDetails.messages.error-title',
          'i18n.domain.deliveryDetails.messages.save-failed'
        );
      }
    });
  }


  async submitDelivery(): Promise<void> {
    if (!this.selectedDeliveryPlan) {
      this.toastService.showError(
        'i18n.domain.deliveryDetails.messages.error-title',
        'No delivery plan selected'
      );
      return;
    }


    if (!this.selectedDeliveryPlan.name?.trim()) {
      this.toastService.showError(
        'i18n.domain.deliveryDetails.messages.error-title',
        'Please enter a delivery plan name'
      );
      return;
    }


    if (this.selectedDeliveryPlan.orderIds.length === 0) {
      this.toastService.showError(
        'i18n.domain.deliveryDetails.messages.error-title',
        'Please add at least one order before submitting'
      );
      return;
    }


    if (!this.selectedDeliveryPlan.assignedToId) {
      this.toastService.showError(
        'i18n.domain.deliveryDetails.messages.error-title',
        'Please assign a driver before submitting'
      );
      return;
    }


    if (!this.selectedDeliveryPlan.deliveryDate) {
      this.toastService.showError(
        'i18n.domain.deliveryDetails.messages.error-title',
        'Please select a delivery date before submitting'
      );
      return;
    }

    try {
      const planId = this.deliveryPlanMap.get(this.selectedDeliveryPlan.id) || 0;
      const dto = new UpdateDeliveryStatusDTO({
        name: this.selectedDeliveryPlan.name,
        assignedToId: this.selectedDeliveryPlan.assignedToId,
        status: 1,
        comments: this.selectedDeliveryPlan.comments,
        orderIds: this.selectedDeliveryPlan.orderIds,
        deliveryDate: this.selectedDeliveryPlan.deliveryDate
          ? DateTime.fromJSDate(this.selectedDeliveryPlan.deliveryDate)
          : undefined
      });

      await firstValueFrom(this.deliveryDetailsService.updateDeliveryPlan(planId, dto));


      await Promise.all(
        this.selectedDeliveryPlan.orderIds.map((orderId) =>
          firstValueFrom(
            this.orderClient.updateOrderStatus(
              new UpdateOrderStatusDTO({ orderId, status: OrderStatus.OutForDelivery })
            )
          )
        )
      );


      this.selectedDeliveryPlan.status = 'out-for-delivery';


      const planInList = this.deliveryPlans.find(p => p.id === this.selectedDeliveryPlan!.id);
      if (planInList) {
        planInList.status = 'out-for-delivery';
      }

      this.toastService.showSuccess(
        'i18n.domain.deliveryDetails.messages.delivery-submitted-title',
        'i18n.domain.deliveryDetails.messages.delivery-submitted-message'
      );

      this.closeDeliveryForm();
      this.loadOrders();
    } catch (error) {
      console.error('Error submitting delivery:', error);
      this.toastService.showError(
        'i18n.domain.deliveryDetails.messages.error-title',
        'i18n.domain.deliveryDetails.messages.delivery-submit-failed'
      );
    }
  }

  getOrdersInPlan(plan: DeliveryPlan): OrderWithLocation[] {
    return this.allOrders.filter((o) => plan.orderIds.includes(o.id!));
  }

  private clearMarkers(): void {
    this.markers.forEach((m) => m.remove());
    this.markers.clear();
  }

  private fitBounds(): void {
    if (this.markers.size === 0) return;

    const bounds = new mapboxgl.LngLatBounds();
    this.markers.forEach((marker) => bounds.extend(marker.getLngLat()));
    this.map.fitBounds(bounds, { padding: 60, maxZoom: 14 });
  }

  formatDateTime(date?: any): string {
    const d = this.toDate(date);
    if (!d) return 'N/A';

    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  formatTime(date?: any): string {
    const d = this.toDate(date);
    return d
      ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
      : 'N/A';
  }

  formatDate(date?: any): string {
    const d = this.toDate(date);
    return d
      ? d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
      : 'N/A';
  }

  private toDate(date?: any): Date | null {
    if (!date) return null;
    if (date.toJSDate) return date.toJSDate();
    try { return new Date(date); } catch { return null; }
  }
}


const CircleMode: any = {
  onSetup: function () {
    const circle = this.newFeature({
      type: 'Feature',
      properties: {
        isCircle: true
      },
      geometry: {
        type: 'Polygon',
        coordinates: [[]]
      }
    });
    this.addFeature(circle);
    this.clearSelectedFeatures();
    this.updateUIClasses({ mouse: 'add' });
    this.setActionableState({ trash: true });
    return {
      circle
    };
  },

  onClick: function (state: any, e: any) {
    if (!state.center) {
      state.center = [e.lngLat.lng, e.lngLat.lat];
    } else {
      const center = state.center;
      const radiusInKm = this.getDistance(center, [e.lngLat.lng, e.lngLat.lat]);

      const circleGeoJSON = circle(center, radiusInKm, {
        steps: 64,
        units: 'kilometers'
      });

      state.circle.incomingCoords(circleGeoJSON.geometry.coordinates);
      state.circle.properties.center = center;
      state.circle.properties.radiusInKm = radiusInKm;

      this.map.fire('draw.create', {
        features: [state.circle.toGeoJSON()]
      });

      this.changeMode('simple_select');
    }
  },

  onMouseMove: function (state: any, e: any) {
    if (state.center) {
      const center = state.center;
      const radiusInKm = this.getDistance(center, [e.lngLat.lng, e.lngLat.lat]);

      const circleGeoJSON = circle(center, radiusInKm, {
        steps: 64,
        units: 'kilometers'
      });

      state.circle.incomingCoords(circleGeoJSON.geometry.coordinates);
    }
  },

  getDistance: function (from: number[], to: number[]): number {
    const R = 6371;
    const lat1 = from[1] * Math.PI / 180;
    const lat2 = to[1] * Math.PI / 180;
    const deltaLat = (to[1] - from[1]) * Math.PI / 180;
    const deltaLng = (to[0] - from[0]) * Math.PI / 180;

    const a = Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
      Math.cos(lat1) * Math.cos(lat2) *
      Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  },

  toDisplayFeatures: function (state: any, geojson: any, display: any) {
    display(geojson);
  },

  onStop: function (state: any) {
    this.deleteFeature([state.circle.id], { silent: true });
  },

  onTrash: function (state: any) {
    this.deleteFeature([state.circle.id], { silent: true });
    this.changeMode('simple_select');
  }


};